import { useTranslation } from "react-i18next";
import { GetOneExpeditionBoResponseDto } from "@/types/api/api-types";
import { ExpeditionBlockedReasonEnum } from "@/constants/enums/expeditionBlockedReason.enum";
import { resolveDeliveryStatusGroup } from "@/components/shared/notificationItem";
import { useExpeditionBlockedReasonLabels } from "@/hooks/useEnumLabels";
import { InfoAccordionRow, InfoRow, InfoRowNested } from "./infoCard";
import { formatDate, getFullName } from "./expeditionDetail.utils";

/** Delivered / blocked / failed date (with reason), plus the deletion date for deleted expeditions. */
export const ExpeditionStatusRow = ({
  expedition,
  isDeleted,
}: {
  expedition: GetOneExpeditionBoResponseDto;
  isDeleted: boolean;
}) => {
  const { t } = useTranslation(["boExpeditions", "common"]);
  const blockedReasonLabels = useExpeditionBlockedReasonLabels();
  const statusGroup = resolveDeliveryStatusGroup(expedition.deliveryStatus);
  const deletedByName = expedition.deletedBy
    ? getFullName(expedition.deletedBy.firstName, expedition.deletedBy.lastName)
    : null;
  const deletedAt = expedition.deletedAt
    ? `${formatDate(expedition.deletedAt, "dd/MM/yyyy à HH:mm")}${deletedByName ? ` (${deletedByName})` : ""}`
    : null;
  const deletedRow = isDeleted ? (
    <InfoRow label={t("expeditionDrawerDetails.deletedAt")} value={deletedAt} />
  ) : null;

  switch (statusGroup) {
    case "delivered":
      return (
        <>
          <InfoRow
            label={t("expeditionDrawerDetails.deliveredAt")}
            value={formatDate(expedition.deliveryDate, "dd/MM/yyyy à HH:mm")}
          />
          {deletedRow}
        </>
      );
    case "blocked":
    case "failed": {
      const reason = expedition.blockedReason
        ? blockedReasonLabels[expedition.blockedReason as ExpeditionBlockedReasonEnum]
        : null;
      return (
        <>
          <InfoAccordionRow
            label={
              statusGroup === "failed"
                ? t("expeditionDrawerDetails.failedAt")
                : t("expeditionDrawerDetails.blockedAt")
            }
            value={formatDate(expedition.blockedAt, "dd/MM/yyyy à HH:mm")}
          >
            {reason && (
              <InfoRowNested
                label={
                  statusGroup === "failed"
                    ? t("expeditionDrawerDetails.failedReason")
                    : t("expeditionDrawerDetails.blockedReason")
                }
                value={reason}
              />
            )}
          </InfoAccordionRow>
          {deletedRow}
        </>
      );
    }
    default:
      return deletedRow;
  }
};
